import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import DealflowDevelopmentLineChart
  from 'components/organisms/Charts/DealflowDevelopmentLineChart/DealflowDevelopmentLineChart';
import StyledDealflowDevelopmentGraphCard
  from 'components/organisms/Cards/DealflowDevelopmentGraphCard/DealflowDevelopmentGraphCard.styles';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import {useClientStatisticDealflowDevelopment} from 'services/client/statistic/statistic.utils';
import Box from 'components/atoms/Layout/Box/Box';
import {P} from 'components/atoms/Text/Typography/Typography';
import {useLinkNavigate} from 'helpers/hooks/links';
import utils from 'helpers/utils';
import SimpleLoader from 'components/molecules/Loaders/SimpleLoader/SimpleLoader';

const DealflowDevelopmentGraphCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'DealflowDevelopmentGraphCard');

  const tableProvider = useTable();
  const graphState = tableProvider.graphState;

  const navigate = useLinkNavigate();

  const [dealflow, setDealflow] = useState(null);

  const listState = useEffectItem(tableProvider.appliedListState());
  const years = graphState.graphSettings['development']?.filter?.years ?? 3;

  const [dealflowDevelopment, isLoading] = useClientStatisticDealflowDevelopment(
    years,
    listState.search,
    listState.filter
  );

  useEffect(() => {
    if (dealflowDevelopment) {
      setDealflow(dealflowDevelopment);
    }
  }, [dealflowDevelopment]);

  const fields = useMemo(() => {
    const initialOption = constants.data.periodYears.find((opt) => opt.value === years);

    return [{
      name: 'years',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.text,
      placeholder: 'Select a period',
      initial: initialOption,
      FormFieldProps: {
        autoFocus: false,
        hiddenLabel: true,
        clearable: false,
        size: 'small'
      },
      options: constants.data.periodYears
    }];
  }, [years]);

  const setGraphSettingsEvent = useEffectEvent(graphState.setGraphSettings);
  const handleFilterChange = useCallback((field, value) => {
    setGraphSettingsEvent?.((current) => ({
      ...current,
      'development': {
        ...current?.['development'],
        filter: {
          ...current?.['development']?.filter,
          years: value?.value ?? value
        }
      }
    }));
  }, [setGraphSettingsEvent]);

  const handleVisibilityChange = (visibility) => {
    graphState.setGraphSettings((current) => ({
      ...current,
      'development': {
        ...current?.['development'],
        visibility
      }
    }));
  }

  const handleClick = (e, release, data, dataKey) => {
    const year = +dataKey;
    let month = data[`${dataKey}-meta`].month;
    if (year && month) {
      month = new Date(month);
      navigate({
        event: e,
        to: `/dealflow/table?custom=dealflowDevelopment:${year}${(month.getMonth() + 1) < 10 ? '0' : ''}${month.getMonth() + 1}`,
        keepSearchParams: true
      });
    }
  };

  return <StyledDealflowDevelopmentGraphCard ref={ref} {...innerProps}
                                             title="Deal flow development"
                                             context={(fields ? <InlineForm onChange={handleFilterChange}
                                                                            fields={fields} /> : null)}>
    {(utils.isDefined(dealflow) && dealflow.length > 0) ?
      <DealflowDevelopmentLineChart dealflow={dealflow}
                                    showLegend={true}
                                    isLoading={isLoading}
                                    onClick={handleClick}
                                    visibility={graphState.graphSettings['development']?.visibility}
                                    onVisibilityChange={handleVisibilityChange} /> : null}

    {(!utils.isDefined(dealflow) || dealflow.length === 0) ? (
      (!utils.isDefined(dealflow) || isLoading) ?
        <SimpleLoader className="GraphCard-loader" /> :
        <Box className="GraphCard-empty">
          <P>No deal flow data found</P>
        </Box>
    ) : null}
  </StyledDealflowDevelopmentGraphCard>
});

DealflowDevelopmentGraphCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default DealflowDevelopmentGraphCard;
